import { useState, useRef, useEffect } from 'react';
import { sendAIMessage, getAIChatHistory } from '../services/api';
import { toast } from 'sonner';
import { Send, Bot, User, Loader2, Sparkles, X, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

export default function AIAdminChat({ isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadHistory();
      setTimeout(focusInput, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  function focusInput() {
    if (inputRef.current) inputRef.current.focus();
  }

  function scrollToBottom() {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  async function loadHistory() {
    try {
      const data = await getAIChatHistory(sessionId, 20);
      const history = data.history || [];
      const formatted = [];
      for (let i = 0; i < history.length; i++) {
        formatted.push({ role: 'user', content: history[i].user_message });
        formatted.push({ role: 'assistant', content: history[i].ai_response });
      }
      setMessages(formatted);
      if (data.session_id) setSessionId(data.session_id);
    } catch (e) {
      console.error('Failed to load chat history');
    }
  }

  async function handleSend() {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(m => m.concat([{ role: 'user', content: text }]));
    setLoading(true);

    try {
      const res = await sendAIMessage(text, sessionId);
      setMessages(m => m.concat([{ 
        role: 'assistant', 
        content: res.response 
      }]));
      if (res.session_id) setSessionId(res.session_id);
      
      const results = res.action_results || [];
      for (let i = 0; i < results.length; i++) {
        if (results[i].success) toast.success(results[i].message);
        else toast.error(results[i].message);
      }
    } catch (e) {
      toast.error('Failed to send message');
      setMessages(m => m.concat([{ 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.',
        isError: true
      }]));
    } finally {
      setLoading(false);
    }
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  if (!isOpen) return null;

  const chatW = isExpanded ? 'w-[600px]' : 'w-[400px]';
  const chatH = isExpanded ? 'h-[600px]' : 'h-[500px]';

  return (
    <div className={`fixed bottom-4 right-4 ${chatW} ${chatH} bg-[rgb(15,15,20)] rounded-2xl border border-white/10 shadow-2xl flex flex-col z-50 overflow-hidden`}>
      <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-orange-500/20 to-red-500/20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-xl">
            🤖
          </div>
          <div>
            <h3 className="font-semibold text-white">Sabooba</h3>
            <p className="text-xs text-white/60">AI Admin • Web Search Enabled</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setIsExpanded(!isExpanded)} className="text-white/60 hover:text-white">
            {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white/60 hover:text-white">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1 p-4">
        {messages.length === 0 ? (
          <EmptyState setInput={setInput} />
        ) : (
          <MessagesList messages={messages} loading={loading} messagesEndRef={messagesEndRef} />
        )}
      </ScrollArea>

      <div className="p-4 border-t border-white/10">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask Sabooba anything..."
            className="flex-1 bg-white/5 border-white/10 text-white"
            disabled={loading}
          />
          <Button onClick={handleSend} disabled={loading || !input.trim()} className="bg-orange-500 hover:bg-orange-600">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ setInput }) {
  return (
    <div className="text-center py-8">
      <div className="text-5xl mb-3">🤖</div>
      <h4 className="font-medium text-white mb-2">Hey! I'm Sabooba</h4>
      <p className="text-sm text-white/60 mb-4">Your AI assistant with web search powers!</p>
      <div className="space-y-2 text-left max-w-[280px] mx-auto">
        <p className="text-xs text-white/40">Try asking:</p>
        <SuggestBtn text="What are my platform stats?" onClick={() => setInput("What are my platform stats?")} />
        <SuggestBtn text="Search for free IPTV playlists" onClick={() => setInput("Search for free IPTV playlists")} />
        <SuggestBtn text="Find US local channels M3U" onClick={() => setInput("Find US local channels M3U")} />
        <SuggestBtn text="Trigger a channel scan" onClick={() => setInput("Trigger a channel scan")} />
      </div>
    </div>
  );
}

function SuggestBtn({ text, onClick }) {
  return (
    <button onClick={onClick} className="w-full text-left text-sm p-2 rounded bg-white/5 hover:bg-white/10 text-white/80">
      {text}
    </button>
  );
}

function MessagesList({ messages, loading, messagesEndRef }) {
  const items = [];
  for (let i = 0; i < messages.length; i++) {
    items.push(<MessageBubble key={i} msg={messages[i]} />);
  }
  if (loading) {
    items.push(
      <div key="loading" className="flex gap-3">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
          <Bot className="w-4 h-4 text-white" />
        </div>
        <div className="bg-white/10 rounded-2xl p-3">
          <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
        </div>
      </div>
    );
  }
  items.push(<div key="end" ref={messagesEndRef} />);
  return <div className="space-y-4">{items}</div>;
}

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  const bg = isUser ? 'bg-purple-500 text-white' : msg.isError ? 'bg-red-500/20 text-red-300' : 'bg-white/10 text-white';
  
  return (
    <div className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
          <Bot className="w-4 h-4 text-white" />
        </div>
      )}
      <div className={`max-w-[80%] rounded-2xl p-3 ${bg}`}>
        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
      </div>
      {isUser && (
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  );
}
